import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, SearchIcon, FilterIcon, DownloadIcon, XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export function TransactionMonitoring() {
  const [timeWindow, setTimeWindow] = useState<'live' | '15m' | '1h'>('live');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'success' | 'failed'>('all');
  const [selectedTxnType, setSelectedTxnType] = useState<'all' | 'p2p' | 'p2m'>('all');
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const liveUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Bank information - hardcoded for this bank portal
  const bankInfo = {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  };
  // Initial sample data - this would typically come from an API
  const initialTransactions = [{
    id: 1,
    dateTime: '2023-05-10 14:30:22',
    senderBankBIC: bankInfo.code,
    senderBankName: bankInfo.name,
    senderAccount: '1000123456789',
    receiverBankBIC: 'CBETETAA',
    receiverBankName: 'Commercial Bank of Ethiopia (CBE)',
    receiverAccount: '2000987654321',
    amount: 5000,
    purposeCode: 'P2P',
    txnType: 'P2P',
    status: 'Success',
    failureReason: '',
    narration: 'Payment for services'
  }, {
    id: 2,
    dateTime: '2023-05-10 14:28:15',
    senderBankBIC: bankInfo.code,
    senderBankName: bankInfo.name,
    senderAccount: '1000223456789',
    receiverBankBIC: 'DASHETAA',
    receiverBankName: 'Dashen Bank S.C.',
    receiverAccount: '2000887654321',
    amount: 3500,
    purposeCode: 'BILL',
    txnType: 'P2M',
    status: 'Failed',
    failureReason: 'Insufficient funds',
    narration: 'Utility payment'
  }, {
    id: 3,
    dateTime: '2023-05-10 14:25:47',
    senderBankBIC: 'BUNAETAA',
    senderBankName: 'Bunna International Bank S.C.',
    senderAccount: '1000323456789',
    receiverBankBIC: bankInfo.code,
    receiverBankName: bankInfo.name,
    receiverAccount: '2000787654321',
    amount: 2800,
    purposeCode: 'P2P',
    txnType: 'P2P',
    status: 'Success',
    failureReason: '',
    narration: 'Loan repayment'
  }, {
    id: 4,
    dateTime: '2023-05-10 14:20:33',
    senderBankBIC: 'NIBIETAA',
    senderBankName: 'NIB International Bank S.C. (NIB)',
    senderAccount: '1000423456789',
    receiverBankBIC: bankInfo.code,
    receiverBankName: bankInfo.name,
    receiverAccount: '2000687654321',
    amount: 4200,
    purposeCode: 'SALARY',
    txnType: 'P2P',
    status: 'Success',
    failureReason: '',
    narration: 'Monthly salary'
  }, {
    id: 5,
    dateTime: '2023-05-10 14:15:19',
    senderBankBIC: bankInfo.code,
    senderBankName: bankInfo.name,
    senderAccount: '1000523456789',
    receiverBankBIC: 'WEGAETAA',
    receiverBankName: 'Wegagen Bank S.C.',
    receiverAccount: '2000587654321',
    amount: 1500,
    purposeCode: 'P2P',
    txnType: 'P2M',
    status: 'Failed',
    failureReason: 'Account not found',
    narration: 'Refund'
  }];
  // Function to generate a random transaction for real-time updates
  const generateRandomTransaction = (id: number) => {
    const otherBanks = [{
      code: 'AWINETAA',
      name: 'Awash Bank S.C.'
    }, {
      code: 'CBETETAA',
      name: 'Commercial Bank of Ethiopia (CBE)'
    }, {
      code: 'BUNAETAA',
      name: 'Bunna International Bank S.C.'
    }, {
      code: 'DASHETAA',
      name: 'Dashen Bank S.C.'
    }, {
      code: 'CBORETAA',
      name: 'Cooperative Bank of Oromia S.C. (COOP)'
    }, {
      code: 'ZEMEETAA',
      name: 'Zemen Bank S.C.'
    }, {
      code: 'NIBIETAA',
      name: 'NIB International Bank S.C. (NIB)'
    }, {
      code: 'HIJRETAA',
      name: 'Hibret Bank S.C.'
    }, {
      code: 'TSCPETAA',
      name: 'Tsehay Bank S.C.'
    }, {
      code: 'WEGAETAA',
      name: 'Wegagen Bank S.C.'
    }, {
      code: 'ABAYETAA',
      name: 'Abay Bank S.C.'
    }];
    const otherBankIndex = Math.floor(Math.random() * otherBanks.length);
    const otherBank = otherBanks[otherBankIndex];
    const isSender = Math.random() > 0.5;
    const senderBank = isSender ? bankInfo : otherBank;
    const receiverBank = isSender ? otherBank : bankInfo;
    const status = Math.random() > 0.8 ? 'Failed' : 'Success';
    const txnType = Math.random() > 0.4 ? 'P2P' : 'P2M';
    const purposeCodes = ['P2P', 'BILL', 'SALARY', 'TRANSFER', 'PAYMENT'];
    const purposeCode = purposeCodes[Math.floor(Math.random() * purposeCodes.length)];
    const amount = Math.floor(Math.random() * 10000) + 100;
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const dateTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${hours}:${minutes}:${seconds}`;
    return {
      id,
      dateTime,
      senderBankBIC: senderBank.code,
      senderBankName: senderBank.name,
      senderAccount: `1000${Math.floor(Math.random() * 1000000000)}`,
      receiverBankBIC: receiverBank.code,
      receiverBankName: receiverBank.name,
      receiverAccount: `2000${Math.floor(Math.random() * 1000000000)}`,
      amount,
      purposeCode,
      txnType,
      status,
      failureReason: status === 'Failed' ? ['Insufficient funds', 'Account not found', 'Transaction timeout', 'Invalid account'][Math.floor(Math.random() * 4)] : '',
      narration: ['Payment for services', 'Utility payment', 'Loan repayment', 'Monthly salary', 'Refund', 'Transfer', 'Bill payment'][Math.floor(Math.random() * 7)]
    };
  };
  // Generate 1000 sample transactions for pagination
  useEffect(() => {
    setLoading(true);
    const generatedTransactions = [...initialTransactions];
    // Generate more transactions to reach 1000
    for (let i = 6; i <= 1000; i++) {
      generatedTransactions.push(generateRandomTransaction(i));
    }
    setTransactions(generatedTransactions);
    setLoading(false);
  }, []);
  // Handle live updates
  useEffect(() => {
    if (timeWindow === 'live') {
      liveUpdateIntervalRef.current = setInterval(() => {
        setTransactions(prev => {
          const newTransaction = generateRandomTransaction(prev.length + 1);
          return [newTransaction, ...prev];
        });
      }, 5000); // Add a new transaction every 5 seconds
    } else {
      if (liveUpdateIntervalRef.current) {
        clearInterval(liveUpdateIntervalRef.current);
      }
    }
    return () => {
      if (liveUpdateIntervalRef.current) {
        clearInterval(liveUpdateIntervalRef.current);
      }
    };
  }, [timeWindow]);
  const handleRowClick = (transaction: any) => {
    setSelectedTransaction(transaction);
    setShowDetails(true);
  };
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };
  // Apply filters to transactions
  const filteredTransactions = transactions.filter(transaction => {
    // Apply search filter
    const searchLower = searchTerm.toLowerCase();
    if (searchTerm && !transaction.senderBankBIC.toLowerCase().includes(searchLower) && !transaction.senderBankName.toLowerCase().includes(searchLower) && !transaction.receiverBankBIC.toLowerCase().includes(searchLower) && !transaction.receiverBankName.toLowerCase().includes(searchLower) && !transaction.senderAccount.toLowerCase().includes(searchLower) && !transaction.receiverAccount.toLowerCase().includes(searchLower) && !transaction.purposeCode.toLowerCase().includes(searchLower)) {
      return false;
    }
    // Apply status filter
    if (selectedStatus !== 'all' && selectedStatus === 'success' && transaction.status !== 'Success') {
      return false;
    }
    if (selectedStatus !== 'all' && selectedStatus === 'failed' && transaction.status !== 'Failed') {
      return false;
    }
    // Apply transaction type filter
    if (selectedTxnType !== 'all' && selectedTxnType === 'p2p' && transaction.txnType !== 'P2P') {
      return false;
    }
    if (selectedTxnType !== 'all' && selectedTxnType === 'p2m' && transaction.txnType !== 'P2M') {
      return false;
    }
    return true;
  });
  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTransactions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };
  return <div className="space-y-6" data-id="element-1752">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1753">
        <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-1754">
          Transaction Monitoring
        </h1>
        <div className="flex items-center gap-3" data-id="element-1755">
          <button onClick={() => setTimeWindow('live')} className={`px-3 py-1 text-sm rounded-md ${timeWindow === 'live' ? 'bg-[#F58220] text-white' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1756">
            Live
          </button>
          <button onClick={() => setTimeWindow('15m')} className={`px-3 py-1 text-sm rounded-md ${timeWindow === '15m' ? 'bg-[#F58220] text-white' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1757">
            15m
          </button>
          <button onClick={() => setTimeWindow('1h')} className={`px-3 py-1 text-sm rounded-md ${timeWindow === '1h' ? 'bg-[#F58220] text-white' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1758">
            1h
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1759">
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1760">
          <div className="flex flex-wrap items-center gap-3" data-id="element-1761">
            <div className="flex items-center gap-2" data-id="element-1762">
              <button onClick={() => setSelectedStatus('all')} className={`px-3 py-1 text-xs rounded-full ${selectedStatus === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1763">
                All Status
              </button>
              <button onClick={() => setSelectedStatus('success')} className={`px-3 py-1 text-xs rounded-full ${selectedStatus === 'success' ? 'bg-green-100 text-green-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1764">
                Success
              </button>
              <button onClick={() => setSelectedStatus('failed')} className={`px-3 py-1 text-xs rounded-full ${selectedStatus === 'failed' ? 'bg-red-100 text-red-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1765">
                Failed
              </button>
            </div>
            <div className="flex items-center gap-2 ml-2" data-id="element-1766">
              <button onClick={() => setSelectedTxnType('all')} className={`px-3 py-1 text-xs rounded-full ${selectedTxnType === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1767">
                All Types
              </button>
              <button onClick={() => setSelectedTxnType('p2p')} className={`px-3 py-1 text-xs rounded-full ${selectedTxnType === 'p2p' ? 'bg-blue-100 text-blue-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1768">
                P2P
              </button>
              <button onClick={() => setSelectedTxnType('p2m')} className={`px-3 py-1 text-xs rounded-full ${selectedTxnType === 'p2m' ? 'bg-purple-100 text-purple-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1769">
                P2M
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-1770">
            <div className="relative flex-grow" data-id="element-1771">
              <input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearch} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1772" />
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" data-id="element-1773" />
            </div>
            <button className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1774">
              <DownloadIcon size={16} className="mr-2" data-id="element-1775" />
              Export
            </button>
          </div>
        </div>
        {loading ? <div className="p-8 text-center" data-id="element-1776">
            <p className="text-gray-500" data-id="element-1777">Loading transactions...</p>
          </div> : <>
            <div className="overflow-x-auto" data-id="element-1778">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1779">
                <thead className="bg-gray-50 sticky top-0" data-id="element-1780">
                  <tr data-id="element-1781">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1782">
                      Date Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1783">
                      Sender Bank BIC
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1784">
                      Sender Bank Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1785">
                      Sender Account
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1786">
                      Receiver Bank BIC
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1787">
                      Receiver Bank Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1788">
                      Receiver Account
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1789">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1790">
                      Txn Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1791">
                      Purpose Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1792">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1793">
                  {currentItems.length > 0 ? currentItems.map(transaction => <tr key={transaction.id} onClick={() => handleRowClick(transaction)} className={`cursor-pointer hover:bg-gray-50 ${transaction.status === 'Failed' ? 'bg-red-50' : ''}`} data-id="element-1794">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1795">
                          {transaction.dateTime}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1796">
                          {transaction.senderBankBIC}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1797">
                          {transaction.senderBankName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1798">
                          {transaction.senderAccount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1799">
                          {transaction.receiverBankBIC}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1800">
                          {transaction.receiverBankName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1801">
                          {transaction.receiverAccount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1802">
                          ETB {transaction.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1803">
                          <span className={`px-2 py-1 text-xs rounded-full ${transaction.txnType === 'P2P' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-1804">
                            {transaction.txnType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1805">
                          {transaction.purposeCode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1806">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1807">
                            {transaction.status}
                          </span>
                        </td>
                      </tr>) : <tr data-id="element-1808">
                      <td colSpan={11} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1809">
                        No transactions found matching your criteria
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 flex flex-col sm:flex-row justify-between items-center border-t border-gray-200" data-id="element-1810">
              <div className="flex items-center mb-4 sm:mb-0" data-id="element-1811">
                <span className="text-sm text-gray-700 mr-2" data-id="element-1812">
                  Showing {indexOfFirstItem + 1}-
                  {Math.min(indexOfLastItem, filteredTransactions.length)} of{' '}
                  {filteredTransactions.length} transactions
                </span>
                <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="ml-2 border border-gray-300 rounded-md text-sm py-1 px-2" data-id="element-1813">
                  <option value={10} data-id="element-1814">10 per page</option>
                  <option value={25} data-id="element-1815">25 per page</option>
                  <option value={50} data-id="element-1816">50 per page</option>
                  <option value={100} data-id="element-1817">100 per page</option>
                </select>
              </div>
              <div className="flex items-center space-x-2" data-id="element-1818">
                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className={`px-2 py-1 text-sm rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1819">
                  First
                </button>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`p-1 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1820">
                  <ChevronLeftIcon size={16} data-id="element-1821" />
                </button>
                <div className="flex items-center space-x-1" data-id="element-1822">
                  {Array.from({
                length: Math.min(5, totalPages)
              }, (_, i) => {
                // Show 5 pages around current page
                let pageNum;
                if (totalPages <= 5) {
                  // Less than 5 pages total, show all
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  // Near the start
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  // Near the end
                  pageNum = totalPages - 4 + i;
                } else {
                  // In the middle
                  pageNum = currentPage - 2 + i;
                }
                return <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`w-8 h-8 flex items-center justify-center rounded-md ${currentPage === pageNum ? 'bg-[#F58220] text-white' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1823">
                          {pageNum}
                        </button>;
              })}
                  {totalPages > 5 && currentPage < totalPages - 2 && <>
                      <span className="px-1" data-id="element-1824">...</span>
                      <button onClick={() => handlePageChange(totalPages)} className="w-8 h-8 flex items-center justify-center rounded-md text-gray-700 hover:bg-gray-100" data-id="element-1825">
                        {totalPages}
                      </button>
                    </>}
                </div>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`p-1 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1826">
                  <ChevronRightIcon size={16} data-id="element-1827" />
                </button>
                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className={`px-2 py-1 text-sm rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1828">
                  Last
                </button>
              </div>
            </div>
          </>}
      </div>
      {showDetails && selectedTransaction && <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-10" data-id="element-1829">
          <div className="bg-white w-full max-w-md h-full overflow-auto" data-id="element-1830">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1831">
              <h3 className="font-medium text-[#1A1F36]" data-id="element-1832">
                Transaction Details
              </h3>
              <button onClick={() => setShowDetails(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1833">
                <XIcon size={20} data-id="element-1834" />
              </button>
            </div>
            <div className="p-4 space-y-4" data-id="element-1835">
              <div data-id="element-1836">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1837">
                  Status
                </h4>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedTransaction.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1838">
                  {selectedTransaction.status}
                </span>
                {selectedTransaction.failureReason && <p className="mt-1 text-sm text-red-600" data-id="element-1839">
                    {selectedTransaction.failureReason}
                  </p>}
              </div>
              <div data-id="element-1840">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1841">
                  Date & Time
                </h4>
                <p className="text-sm text-gray-900" data-id="element-1842">
                  {selectedTransaction.dateTime}
                </p>
              </div>
              <div data-id="element-1843">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1844">
                  Transaction Type
                </h4>
                <p className="text-sm" data-id="element-1845">
                  <span className={`px-2 py-1 text-xs rounded-full ${selectedTransaction.txnType === 'P2P' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-1846">
                    {selectedTransaction.txnType}
                  </span>
                </p>
              </div>
              <div data-id="element-1847">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1848">
                  Amount
                </h4>
                <p className="text-lg font-semibold text-[#1A1F36]" data-id="element-1849">
                  ETB {selectedTransaction.amount.toLocaleString()}
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1850">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1851">
                  Sender Details
                </h4>
                <div className="space-y-2" data-id="element-1852">
                  <div data-id="element-1853">
                    <p className="text-xs text-gray-500" data-id="element-1854">Bank BIC</p>
                    <p className="text-sm text-gray-900" data-id="element-1855">
                      {selectedTransaction.senderBankBIC}
                    </p>
                  </div>
                  <div data-id="element-1856">
                    <p className="text-xs text-gray-500" data-id="element-1857">Bank Name</p>
                    <p className="text-sm text-gray-900" data-id="element-1858">
                      {selectedTransaction.senderBankName}
                    </p>
                  </div>
                  <div data-id="element-1859">
                    <p className="text-xs text-gray-500" data-id="element-1860">Account</p>
                    <p className="text-sm text-gray-900" data-id="element-1861">
                      {selectedTransaction.senderAccount}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1862">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1863">
                  Receiver Details
                </h4>
                <div className="space-y-2" data-id="element-1864">
                  <div data-id="element-1865">
                    <p className="text-xs text-gray-500" data-id="element-1866">Bank BIC</p>
                    <p className="text-sm text-gray-900" data-id="element-1867">
                      {selectedTransaction.receiverBankBIC}
                    </p>
                  </div>
                  <div data-id="element-1868">
                    <p className="text-xs text-gray-500" data-id="element-1869">Bank Name</p>
                    <p className="text-sm text-gray-900" data-id="element-1870">
                      {selectedTransaction.receiverBankName}
                    </p>
                  </div>
                  <div data-id="element-1871">
                    <p className="text-xs text-gray-500" data-id="element-1872">Account</p>
                    <p className="text-sm text-gray-900" data-id="element-1873">
                      {selectedTransaction.receiverAccount}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1874">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1875">
                  Additional Information
                </h4>
                <div className="space-y-2" data-id="element-1876">
                  <div data-id="element-1877">
                    <p className="text-xs text-gray-500" data-id="element-1878">Purpose Code</p>
                    <p className="text-sm text-gray-900" data-id="element-1879">
                      {selectedTransaction.purposeCode}
                    </p>
                  </div>
                  <div data-id="element-1880">
                    <p className="text-xs text-gray-500" data-id="element-1881">Narration</p>
                    <p className="text-sm text-gray-900" data-id="element-1882">
                      {selectedTransaction.narration}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}